import React from 'react';
import { array, bool, func, number, object, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { propTypes } from '../../util/types';

import {
  sendVerificationEmail,
  hasCurrentUserErrors,
  getCurrentUserTotalNotificationCount,
} from '../../ducks/user.duck';
import {
  logout,
  login,
  signup,
  authenticationInProgress,
} from '../../ducks/auth.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import {
  fetchSearchSuggestions,
  clearSuggestions,
} from './TopbarContainer.duck';
import Topbar from './Topbar/Topbar';

export const TopbarContainerComponent = props => {
  const {
    authInProgress,
    currentPage,
    currentSearchParams,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    history,
    isAuthenticated,
    isLoggedInAs,
    authScopes,
    hasGenericError,
    location,
    notificationCount,
    listingsCount,
    favoritesCount,
    savedSearchesCount,
    onLogout,
    onManageDisableScrolling,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    onResendVerificationEmail,
    isScrolled,
    ...rest
  } = props;

  return (
    <Topbar
      authInProgress={authInProgress}
      currentPage={currentPage}
      currentSearchParams={currentSearchParams}
      currentUser={currentUser}
      currentUserHasListings={currentUserHasListings}
      currentUserHasOrders={currentUserHasOrders}
      history={history}
      isAuthenticated={isAuthenticated}
      isLoggedInAs={isLoggedInAs}
      authScopes={authScopes}
      location={location}
      notificationCount={notificationCount}
      listingsCount={listingsCount}
      favoritesCount={favoritesCount}
      savedSearchesCount={savedSearchesCount}
      onLogout={onLogout}
      onLogin={props.onLogin}
      onSignup={props.onSignup}
      loginError={props.loginError}
      signupError={props.signupError}
      onManageDisableScrolling={onManageDisableScrolling}
      onResendVerificationEmail={onResendVerificationEmail}
      sendVerificationEmailInProgress={sendVerificationEmailInProgress}
      sendVerificationEmailError={sendVerificationEmailError}
      showGenericError={hasGenericError}
      isScrolled={isScrolled}
      {...rest}
    />
  );
};

TopbarContainerComponent.defaultProps = {
  currentPage: null,
  currentSearchParams: null,
  currentUser: null,
  currentUserHasOrders: null,
  notificationCount: 0,
  favoritesCount: 0,
  savedSearchesCount: 0,
  sendVerificationEmailError: null,
  authScopes: null,
  loginError: null,
  signupError: null,
};

TopbarContainerComponent.propTypes = {
  authInProgress: bool.isRequired,
  currentPage: string,
  currentSearchParams: object,
  currentUser: propTypes.currentUser,
  currentUserHasListings: bool.isRequired,
  currentUserHasOrders: bool,
  isAuthenticated: bool.isRequired,
  isLoggedInAs: bool.isRequired,
  authScopes: array,
  notificationCount: number,
  favoritesCount: number,
  savedSearchesCount: number,
  onLogout: func.isRequired,
  onLogin: func.isRequired,
  onSignup: func.isRequired,
  loginError: propTypes.error,
  signupError: propTypes.error,
  onManageDisableScrolling: func.isRequired,
  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  onResendVerificationEmail: func.isRequired,
  hasGenericError: bool.isRequired,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({ state: object }).isRequired,
};

const mapStateToProps = state => {
  // Topbar needs isAuthenticated and isLoggedInAs
  const {
    isAuthenticated,
    isLoggedInAs,
    logoutError,
    authScopes,
    loginError,
    signupError,
  } = state.auth;
  // Topbar needs user info.
  const {
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentUserListingsCount: listingsCount,
    currentUserFavoritesCount: favoritesCount,
    currentUserSavedSearchesCount: savedSearchesCount,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
  } = state.user;
  const notificationCount = getCurrentUserTotalNotificationCount(state);
  const hasGenericError = !!(logoutError || hasCurrentUserErrors(state));
  // Live listing suggestions for the keyword autocomplete.
  const { suggestionListings, suggestionsInProgress } = state.TopbarContainer;
  return {
    suggestionListings,
    suggestionsInProgress,
    authInProgress: authenticationInProgress(state),
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    notificationCount,
    listingsCount,
    favoritesCount,
    savedSearchesCount,
    isAuthenticated,
    isLoggedInAs,
    authScopes,
    loginError,
    signupError,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    hasGenericError,
  };
};

const mapDispatchToProps = dispatch => ({
  onLogout: historyPush => dispatch(logout(historyPush)),
  onLogin: ({ email, password }) => dispatch(login(email, password)),
  onSignup: params => dispatch(signup(params)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  onFetchSearchSuggestions: keywords =>
    dispatch(fetchSearchSuggestions(keywords)),
  onClearSearchSuggestions: () => dispatch(clearSuggestions()),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const TopbarContainer = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(TopbarContainerComponent);

export default TopbarContainer;
